/**
 * Serialises CV Builder form fields into the nested JSON content hash
 * expected by the CvDocument model.
 *
 * Field naming convention:
 *   - Simple: "personal.full_name" -> { personal: { full_name: value } }
 *   - Array:  "experience[0].title" -> { experience: [{ title: value }] }
 *   - Plain:  "summary" -> { summary: value }
 *   - Plain:  "interests" -> { interests: ["a", "b"] } (comma-split)
 *   - Plain:  "referees" -> { referees: value }
 */

/**
 * Serialise all cv-input fields within a container into a content hash.
 * @param {HTMLElement} container
 * @returns {Object} content hash matching CvDocument schema
 */
export function serialise(container) {
  const content = {}
  const inputs = container.querySelectorAll("[name]")

  inputs.forEach((input) => {
    const name = input.name
    const value = readValue(input)

    if (name.includes("[")) {
      setArrayField(content, name, value)
    } else if (name.includes(".")) {
      setNestedField(content, name, value)
    } else {
      setPlainField(content, name, value)
    }
  })

  // Ensure bullets are arrays, not strings
  if (content.experience) {
    content.experience.forEach((entry) => {
      if (typeof entry.bullets === "string") {
        entry.bullets = entry.bullets.split("\n").filter((b) => b.trim())
      }
    })
  }

  // Ensure skill items are arrays
  if (content.skills) {
    content.skills.forEach((group) => {
      if (typeof group.items === "string") {
        group.items = group.items.split(",").map((s) => s.trim()).filter(Boolean)
      }
    })
  }

  // Ensure interests is an array
  if (typeof content.interests === "string") {
    content.interests = content.interests.split(",").map((s) => s.trim()).filter(Boolean)
  }

  // Ensure key_achievements is an array (one per line)
  if (typeof content.key_achievements === "string") {
    content.key_achievements = content.key_achievements.split("\n").map((s) => s.trim()).filter(Boolean)
  }

  // Strip empty section_labels so template defaults apply
  if (content.section_labels) {
    for (const key of Object.keys(content.section_labels)) {
      if (!content.section_labels[key]?.trim()) {
        delete content.section_labels[key]
      }
    }
    if (Object.keys(content.section_labels).length === 0) {
      delete content.section_labels
    }
  }

  // Read section_order from DOM (section-order controller) or build from content
  content.section_order = buildSectionOrder(container, content)

  // Collect page breaks from checked toggles
  content.page_breaks = buildPageBreaks(container)

  return content
}

function readValue(input) {
  if (input.type === "checkbox") return input.checked
  return input.value
}

// "experience[0].title" -> content.experience[0].title = value
function setArrayField(content, name, value) {
  const match = name.match(/^(\w+)\[(\d+)\]\.(\w+)$/)
  if (!match) return

  const [, section, indexStr, field] = match
  const index = parseInt(indexStr, 10)

  if (!content[section]) content[section] = []

  // Extend array if needed
  while (content[section].length <= index) {
    content[section].push({})
  }

  content[section][index][field] = value
}

// "personal.full_name" -> content.personal.full_name = value
function setNestedField(content, name, value) {
  const parts = name.split(".")
  let target = content

  for (let i = 0; i < parts.length - 1; i++) {
    if (!target[parts[i]]) target[parts[i]] = {}
    target = target[parts[i]]
  }

  target[parts[parts.length - 1]] = value
}

// "summary" -> content.summary = value
function setPlainField(content, name, value) {
  content[name] = value
}

function buildPageBreaks(container) {
  const checks = container.querySelectorAll("[data-page-break]")
  return Array.from(checks)
    .filter((cb) => cb.checked)
    .map((cb) => cb.dataset.pageBreak)
}

function buildSectionOrder(container, content) {
  // Read from section-order controller if present
  const orderContainer = container.querySelector(
    "[data-controller*='cv-builder-section-order']"
  )
  if (orderContainer) {
    const sections = orderContainer.querySelectorAll(
      "[data-section-name][data-section-active='true']"
    )
    const order = Array.from(sections).map((s) => s.dataset.sectionName)
    return ["personal", ...order]
  }

  // Fallback: build from content presence
  const order = ["personal"]
  if (content.summary) order.push("summary")
  if (content.experience?.length) order.push("experience")
  if (content.education?.length) order.push("education")
  if (content.skills?.length) order.push("skills")
  if (content.certifications?.length) order.push("certifications")
  if (content.languages?.length) order.push("languages")
  if (content.achievements?.length) order.push("achievements")
  if (content.interests?.length) order.push("interests")
  if (content.referees) order.push("referees")
  return order
}
